import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NodeControls, NodeTitle, OptionalChip, StepNumber, StepStateChip } from "../graph/NodeControls";
import { nodeText } from "../graph/nodeText";
import { stepKindOf } from "../graph/stepKind";
import StepKindMark from "../graph/StepKindMark";
import { classNames } from "../../../ui/styles/classNames";
import { graphLevels, type GraphLevels } from "../model/graphLevels";
import type { GraphRun } from "../model/graphRun";
import type { PlacementAxes } from "../model/placementAxes";
import type { GraphNode, WorkflowGraph } from "../model/workflowGraph";

interface WorkflowOutlineProps {
  graph: WorkflowGraph;
  /** The Run the run graph shows; `null` on the Workflow graph. */
  run: GraphRun | null;
  /** Each step's placement axes by node ID (`placementAxes`, WD-1+3). */
  axes: ReadonlyMap<string, PlacementAxes>;
}

/**
 * The Workflow as nested lists: the same graph, the same open nodes, in reading order. Each
 * level lists its inputs, with the steps they feed, then its pipeline in order. Steps carry
 * the graph's numbers (`workflowGraph`), so the two views and a Run's step list agree.
 * This is the view for keyboard and screen-reader use, and for printing.
 */
export default function WorkflowOutline({ graph, run, axes }: WorkflowOutlineProps) {
  const levels = useMemo(() => graphLevels(graph), [graph]);
  const titles = useTitles(graph, run);
  return (
    <div className="app-card p-4">
      <OutlineLevel parentId={null} levels={levels} run={run} axes={axes} titles={titles} />
    </div>
  );
}

function useTitles(graph: WorkflowGraph, run: GraphRun | null) {
  const { t } = useTranslation();
  return useMemo(() => new Map(graph.nodes.map((node) => [node.id, nodeText(node, run?.get(node.id)?.state ?? null, t)])), [graph, run, t]);
}

interface LevelProps {
  parentId: string | null;
  levels: GraphLevels;
  run: GraphRun | null;
  axes: ReadonlyMap<string, PlacementAxes>;
  titles: ReturnType<typeof useTitles>;
}

function OutlineLevel({ parentId, levels, run, axes, titles }: LevelProps) {
  const { t } = useTranslation();
  const nodes = levels.childrenOf(parentId);
  const inputs = nodes.filter((node) => node.band === "input");
  const pipeline = nodes.filter((node) => node.band === "main");
  const edges = levels.edgesAt(parentId);
  const feedsOf = (id: string) => edges.filter(([source]) => source === id).map(([, target]) => titles.get(target)?.title ?? target);
  return (
    <div className="space-y-3">
      {inputs.length === 0 ? null : (
        <section aria-label={t("workflows.outline.inputs")}>
          <h4 className="app-text-muted text-xs font-semibold">{t("workflows.outline.inputs")}</h4>
          <ul className="mt-1 space-y-2">
            {inputs.map((node) => (
              <OutlineItem key={node.id} node={node} feeds={feedsOf(node.id)} levels={levels} run={run} axes={axes} titles={titles} />
            ))}
          </ul>
        </section>
      )}
      <section aria-label={t("workflows.outline.pipeline")}>
        <h4 className="app-text-muted text-xs font-semibold">{t("workflows.outline.pipeline")}</h4>
        <ol className="mt-1 space-y-2">
          {pipeline.map((node) => (
            <OutlineItem key={node.id} node={node} feeds={[]} levels={levels} run={run} axes={axes} titles={titles} />
          ))}
        </ol>
      </section>
    </div>
  );
}

function OutlineItem({ node, feeds, levels, run, axes, titles }: { node: GraphNode; feeds: readonly string[] } & Omit<LevelProps, "parentId">) {
  const { t } = useTranslation();
  const text = titles.get(node.id);
  const state = run?.get(node.id)?.state ?? null;
  const stepKind = stepKindOf(node);
  const definitionKind = node.kind === "step" && node.step !== null ? node.step.definition.kind : null;
  if (text === undefined) {
    return null;
  }
  return (
    <li className={classNames("space-y-0.5 text-sm", stepKind === null ? "" : `app-step-strip app-step-kind-${stepKind} pl-2`, text.optional === null ? "" : "app-step-optional")}>
      <span className="flex flex-wrap items-baseline gap-2">
        {text.number === null ? null : <StepNumber number={text.number} />}
        {definitionKind === null ? <span className="app-text-muted text-xs">{text.kind}</span> : <StepKindMark kind={definitionKind} needsPerson={axes.get(node.id)?.needsPerson === true} className="self-center" />}
        <NodeTitle node={node} title={text.title} className="underline" />
        {text.optional === null ? null : <OptionalChip text={text.optional} />}
        {state === null ? null : <StepStateChip state={state} />}
      </span>
      {text.fact === null ? null : <span className="app-text-muted block text-xs">{text.fact}</span>}
      {feeds.length === 0 ? null : <span className="app-text-muted block text-xs">{t("workflows.outline.feeds", { steps: feeds.join(", ") })}</span>}
      <NodeControls node={node} title={text.title} />
      {node.kind !== "step" && node.expanded ? (
        <div className="app-flow-outline-nested mt-2 pl-3">
          <OutlineLevel parentId={node.id} levels={levels} run={run} axes={axes} titles={titles} />
        </div>
      ) : null}
    </li>
  );
}
